// src/context/AccountContext.tsx
import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';
import { Account } from '../state/types';
import axios from 'axios';
import { useSession } from './SessionContext';

interface AccountContextType {
  // UI state
  showSignUp: boolean;
  setShowSignUp: (show: boolean) => void;
  selectedAccount: Account | null;
  setSelectedAccount: (account: Account | null) => void;
  isChangePasswordOpen: boolean;
  setIsChangePasswordOpen: (isOpen: boolean) => void;
  openAddAccountModal: () => void;
  openEditAccountModal: (account: Account) => void;
  closeModal: () => void;

  // Data state
  accounts: Account[];

  // API actions (replacing redux thunks)
  fetchAccounts: () => Promise<void>;
  addAccount: (account: Partial<Account>) => Promise<void>;
  updateAccount: (account: Account, currentUser: Account) => Promise<void>;
  deleteAccount: (accountId: string) => Promise<void>;
  resendTempPassword: (username: string, email: string) => Promise<void>;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

const baseUrl = `${process.env.REACT_APP_UMS_API_BASE_URL}/user`
const headers = { Authorization: `admin-role`, 'Content-Type': 'application/json' };

export const AccountProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [showSignUp, setShowSignUp] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  const [accounts, setAccounts] = useState<Account[]>([]);
  const { setIsLoading } = useSession()

  const openAddAccountModal = () => {
    setSelectedAccount(null);
    setShowSignUp(true);
  };

  const openEditAccountModal = (account: Account) => {
    setSelectedAccount(account);
    setShowSignUp(true);
  };

  const closeModal = () => {
    setSelectedAccount(null);
    setShowSignUp(false);
  };

  const fetchAccounts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(baseUrl, { headers });

      const translatedAccounts: Account[] = response.data.users.map((u: any) => ({
        username: u.Username,
        email: u.Attributes.email || 'N/A',
        first_name: u.Attributes.given_name || '',
        last_name: u.Attributes.family_name || '',
        phone_number: u.Attributes.phone_number || '',
        createdDate: new Date(u.UserCreateDate).toLocaleString(),
        lastLoginDate:
          u.LastLoggedInDate === 'Never Logged In'
            ? u.LastLoggedInDate
            : new Date(u.LastLoggedInDate).toLocaleString(),
        status: u.UserStatus,
        role: u.Role || 'Unknown',
        system_access: u.SystemAccess ?? [],
      }));

      setAccounts(translatedAccounts);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const addAccount = async (account: Partial<Account>) => {
    try {
      await axios.post(baseUrl, account, { headers });
      alert('Account added successfully!');
      await fetchAccounts();
    } catch (error) {
      console.error('Error adding account:', error);
      alert('Error adding account. Please check the logs.');
    }
  };

  const updateAccount = async (account: Account, _currentUser: Account) => {
    try {
      const url = `${baseUrl}/${account.username}`;
      await axios.put(url, account, { headers });
      alert('Account updated successfully!');
      await fetchAccounts();
    } catch (error) {
      console.error('Error updating account:', error);
      alert('Error updating account. Please check the logs.');
    }
  };

  const deleteAccount = async (accountId: string) => {
    try {
      const url = `${baseUrl}/${accountId}`;
      await axios.delete(url, { headers });
      alert('Account deleted successfully!');
      await fetchAccounts();
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Error deleting account. Please check the logs.');
    }
  };

  const resendTempPassword = async (username: string, email: string) => {
    try {
      const url = `${baseUrl}/${username}/resend-temp-password`;
      await axios.post(url, { username }, { headers });
      alert(`Temporary password reset email sent for ${email}`);
      await fetchAccounts();
    } catch (error) {
      console.error('Error resending temporary password:', error);
      alert('Failed to resend temporary password. Check logs for details.');
    }
  };

  const value: AccountContextType = {
    // UI state
    showSignUp,
    setShowSignUp,
    selectedAccount,
    setSelectedAccount,
    isChangePasswordOpen,
    setIsChangePasswordOpen,
    openAddAccountModal,
    openEditAccountModal,
    closeModal,

    // data state
    accounts,

    // actions
    fetchAccounts,
    addAccount,
    updateAccount,
    deleteAccount,
    resendTempPassword,
  };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
};

export const useAccountContext = () => {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};